import * as Accordion from '@radix-ui/react-accordion';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import classNames from 'classnames';
import {
	Activity,
	BarChart3,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	HelpCircle,
	Home,
	Landmark,
	LayoutGrid,
	LogOut,
	LucideProps,
	Megaphone,
	Menu,
	MessageCircle,
	MoonStar,
	NotepadText,
	Plus,
	Sheet,
	ShoppingCart,
	SlidersHorizontal,
	SunMedium,
	UserPen,
	UserSearch,
	UserSearchIcon,
	X
} from 'lucide-react';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, Location, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import { logout, UserData } from '../hooks/auth';
import {
	GetRoutePermissions,
	userCanConfigureSellerCommissions,
	userCanManageSellerCommissionPayouts,
	userHasRouteAccess
} from '../hooks/firebase';
import clsx from 'clsx';
import Select from './Select';
import { ucfirst } from '../utils/formatters';
import { type CountrySettings } from '../context/GlobalContext';
import {
	COUNTRIES,
	COUNTRY_LABELS,
	TEMP_DISABLE_FULL_ONLINE_SALES,
	TEMP_DISABLE_SELLER_COMMISSIONS_MENU,
	TEMP_ENABLE_FULL_ONLINE_SALES
} from '../utils/constants';

const getInitialTheme = (): 'dark' | 'light' => {
	const savedTheme = localStorage.getItem('theme');
	if (savedTheme === 'dark' || savedTheme === 'light') {
		return savedTheme;
	}
	// If no theme is saved, default to dark
	return 'dark';
};

const DesktopNavigation = ({
	isOpen,
	setOpen,
	location,
	theme,
	setTheme,
	userData,
	accessToOtherStores,
	helpVideos,
	featureFlags,
	internal,
	internalPath,
	internalCountry,
	permissionsBlacklist,
	permissionsWhitelist,
	routePermissions,
	isDesktopCollapsed,
	setDesktopCollapsed,
	isAdminAccount,
	sellerCommissionsAvailable,
	intercomAvailable,
	intercomOpen,
	openIntercom
}: {
	location: Location;
	isOpen: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	isDesktopCollapsed: boolean;
	setDesktopCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	isAdminAccount: boolean;
	sellerCommissionsAvailable: boolean;
	theme: 'dark' | 'light';
	setTheme: React.Dispatch<React.SetStateAction<'dark' | 'light'>>;
	userData: UserData;
	accessToOtherStores: string;
	helpVideos: CountrySettings['instructions'];
	featureFlags: CountrySettings['feature_flags'];
	internal: boolean;
	internalPath: { get: () => string; set: (path: string) => void };
	internalCountry: { get: () => string; set: (country: string) => void };
	permissionsBlacklist: string[];
	permissionsWhitelist: string[];
	routePermissions: ReturnType<typeof GetRoutePermissions>;
	intercomAvailable: boolean;
	intercomOpen: boolean;
	openIntercom: () => void;
}) => {
	const isInactive = userData?.is_active === false || userData?.master_store?.is_active === false;

	const MenuCategory = ({
		label,
		children,
		Icon
	}: {
		label: string;
		Icon: React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'>>;
		children: ReactNode;
	}) => {
		// Check if children is an array and if it has any items
		const childrenArray = React.Children.toArray(children);
		if (childrenArray.length === 0) {
			return null; // Don't render the category if it has no children
		}

		return (
			<Accordion.Item className="w-full select-none space-y-1.5" value={label}>
				<Accordion.Trigger className="group flex w-full cursor-pointer items-center justify-between text-[1.075rem] font-medium tracking-tight text-neutral-0">
					<div className="flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors group-hover:text-primary-200 group-data-[state=open]:text-primary-200 light:group-hover:text-primary-600 light:group-data-[state=open]:text-primary-600">
						<Icon className="size-5" strokeWidth={2.25} />
						<span>{label}</span>
					</div>

					<ChevronDown
						className="size-4 text-primary-200 transition-transform group-data-[state=closed]:-rotate-90 light:text-primary-600"
						strokeWidth={3}
					/>
				</Accordion.Trigger>
				<Accordion.Content className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
					<ul className="text-[0.9rem] text-neutral-200">{children}</ul>
				</Accordion.Content>
			</Accordion.Item>
		);
	};

	const MenuItem = ({ label, path }: { label: string; path: string }) => (
		<li>
			<Link
				onClick={(e) => {
					if (internal) {
						e.preventDefault();
						internalPath.set(path);
					}
					setOpen(false);
				}}
				to={path}
				className={classNames(
					'block w-full rounded-lg px-2 py-1.5 leading-tight transition-colors hover:bg-primary-500/50 hover:text-neutral-0',
					{
						'bg-primary-500/50 py-1 font-semibold text-neutral-0': !internal
							? location.pathname === path
							: internalPath.get() === path || (label === 'Administrar clientes' && internalPath.get() === '')
					}
				)}
			>
				{label}
			</Link>
		</li>
	);

	const MenuCategories = !internal
		? ['Menu Principal', 'Ajustes', 'Mercadeo', 'Ventas Online', 'Tienda', 'Reportes', 'Administración', 'Ayuda']
		: ['Búsqueda', 'Gestión de Pagos', 'Reportes', 'Finanzas', 'Operaciones', 'Mercadeo', 'Administración'];

	const [scrollPosition, setScrollPosition] = useState(0);
	const scrollAreaRef = useRef<null | HTMLDivElement>(null);

	useEffect(() => {
		const handleScroll = () => {
			if (scrollAreaRef.current) {
				setScrollPosition(scrollAreaRef.current.scrollTop);
			}
		};

		const currentScrollArea = scrollAreaRef.current;
		if (currentScrollArea) {
			currentScrollArea.scrollTop = scrollPosition;
			currentScrollArea.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (currentScrollArea) {
				currentScrollArea.removeEventListener('scroll', handleScroll);
			}
		};
	});

	const selectedCountry = internalCountry.get() || userData?.country;
	const hasAccessToRoute = (route: string) => userHasRouteAccess(userData, routePermissions, route, selectedCountry);
	const canConfigureSellerCommissions = userCanConfigureSellerCommissions(
		userData,
		routePermissions,
		selectedCountry,
		isAdminAccount
	);
	const canManageSellerCommissionPayouts = userCanManageSellerCommissionPayouts(userData, routePermissions, selectedCountry);
	const canAccessSellerCommissions = canConfigureSellerCommissions || canManageSellerCommissionPayouts;

	useEffect(() => {
		const path = internalPath.get();
		const country = internalCountry.get();
		if (path && country && !hasAccessToRoute(path)) {
			internalPath.set('');
		} else if (country && path === '' && !hasAccessToRoute('Administrar clientes')) {
			internalPath.set('/no-access');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [internalCountry.get()]);

	return (
		<nav
			className={classNames(
				'ease-[cubic-bezier(0.87,0,0.13,1)] fixed right-0 z-30 flex h-svh w-72 shrink-0 translate-x-72 flex-col items-center justify-start bg-foreground transition-[width,transform] duration-0 light:border-r-[1.5px] light:border-neutral-800/80 xl:left-0 xl:translate-x-0 xl:duration-300',
				{
					'!translate-x-0 !duration-500': isOpen,
					'xl:w-12': isDesktopCollapsed,
					'xl:w-72': !isDesktopCollapsed
				}
			)}
			onClick={(e) => e.stopPropagation()}
		>
			<div
				className={classNames(
					'flex h-20 w-full items-center justify-between border-b-[1.5px] border-neutral-800/80 px-6 xl:px-0',
					isDesktopCollapsed && 'xl:border-b-0',
					'xl:justify-center'
				)}
			>
				<Link to={'/'}>
					<Logo size="full" className={classNames('w-28', isDesktopCollapsed && 'xl:hidden')} />
				</Link>
				<button
					type="button"
					aria-label={isDesktopCollapsed ? 'Expandir menú' : 'Contraer menú'}
					title={isDesktopCollapsed ? 'Expandir menú' : 'Contraer menú'}
					className={classNames(
						'hidden size-9 items-center justify-center rounded-lg text-primary-200 transition-colors hover:bg-primary-500/20 hover:text-neutral-0 light:text-primary-600 light:hover:bg-primary-500/20',
						internal && (isDesktopCollapsed ? 'xl:flex' : 'xl:absolute xl:right-3 xl:flex')
					)}
					onClick={(e) => {
						e.stopPropagation();
						setDesktopCollapsed((collapsed) => !collapsed);
					}}
				>
					{isDesktopCollapsed ? (
						<ChevronRight className="size-5" strokeWidth={2.5} />
					) : (
						<ChevronLeft className="size-5" strokeWidth={2.5} />
					)}
				</button>
				<X
					strokeWidth={2.66}
					className="size-7 cursor-pointer text-primary-200 transition-opacity hover:opacity-70 light:text-primary-600 xl:hidden"
					onClick={(e) => {
						e.stopPropagation();
						setOpen(false);
					}}
				/>
			</div>

			<ScrollArea.Root className={classNames('h-full w-64 overflow-hidden', isDesktopCollapsed && 'xl:hidden')}>
				<ScrollArea.Viewport ref={scrollAreaRef} className="h-full w-full">
					<Accordion.Root
						type="multiple"
						defaultValue={MenuCategories}
						className="w-full space-y-8 px-6 pb-6 pt-6"
					>
						{!internal && (
							<>
								<MenuCategory label="Menu Principal" Icon={LayoutGrid}>
									<MenuItem label="Inicio" path="/" />
									{!permissionsBlacklist?.includes('new_sale') && !isInactive && (
										<MenuItem label="Nuevo registro" path="/newSale" />
									)}
									{!permissionsBlacklist?.includes('register_payment') && (
										<MenuItem label="Registrar pago" path="/buscarcliente" />
									)}
									{userData?.master_store?.recarga_sales_access === true &&
										!permissionsBlacklist?.includes('recargas') &&
										!isInactive && <MenuItem label="Recargar" path="/tiendas/recargar" />}
									<MenuItem label="Simulador" path="/simulator" />
								</MenuCategory>

								{userData.master_user && (
									<MenuCategory label="Ajustes" Icon={UserPen}>
										<MenuItem label="Perfil" path="/perfil" />
										<MenuItem label="Mis tiendas" path="/sucursales" />
										<MenuItem label="Integraciones" path="/integraciones" />
										{userData.country === 'venezuela' && userData.self_financed && !userData.financed_by_krece && (
											<MenuItem label="Métodos de pago" path="/metodos-de-pago" />
										)}
									</MenuCategory>
								)}

								<MenuCategory label="Mercadeo" Icon={Megaphone}>
									{userData?.master_store?.promotions_portal_access &&
									featureFlags?.promotions_portal &&
									!isInactive &&
									(userData.master_user || permissionsWhitelist?.includes('promotions_portal')) && (
										<MenuItem label="Promociones" path="/mis-promociones" />
									)}
									<MenuItem label="Material publicitario" path="/promociones" />
								</MenuCategory>

								{userData?.online_sales_beta_access === true &&
									userData?.master_store?.online_sales_beta_access === true &&
									!userData?.master_store?.online_sales_access && (
										<MenuCategory label="Ventas Online" Icon={ShoppingCart}>
											<MenuItem label="Órdenes pendientes" path="/ventas-online" />
										</MenuCategory>
									)}

									{!TEMP_DISABLE_FULL_ONLINE_SALES &&
										(userData?.master_store?.online_sales_access === true || TEMP_ENABLE_FULL_ONLINE_SALES) &&
										userData?.master_user === true && (
										<MenuCategory label="Ventas Online" Icon={ShoppingCart}>
											<MenuItem label="Inventario" path="/ventas-online/inventario" />
											<MenuItem label="Catálogos" path="/ventas-online/catalogos" />
										</MenuCategory>
									)}

								<MenuCategory label="Reportes" Icon={BarChart3}>
									{(!userData.self_financed && userData.financed_by_krece) || !userData.master_user ? null : (
										<>
											<MenuItem label="Cuentas por cobrar" path="/cpc" />
										</>
									)}
									{userData?.country !== 'bolivia' && <MenuItem label="Transacciones" path="/reportes/transacciones" />}
									<MenuItem label="Ventas" path="/reportes/ventas" />
									{!TEMP_DISABLE_SELLER_COMMISSIONS_MENU && sellerCommissionsAvailable && (
										<MenuItem
											label={userData.is_seller || userData.seller_user ? 'Mis comisiones' : 'Comisiones vendedores'}
											path="/comisiones-vendedores"
										/>
									)}
									<MenuItem label="Registros eliminados" path="/reportes/registros-eliminados" />
									<MenuItem label="Clientes en mora" path="/reportes/delincuentes" />
									{userData.master_user && <MenuItem label="Salud de tienda" path="/salud" />}
									{userData?.master_store?.cobranza_portal_access &&
									featureFlags?.cobranza_portal &&
									(userData.master_user || permissionsWhitelist?.includes('cobranza_portal')) && (
										<MenuItem label="Cobranza" path="/recuperacion-cartera" />
									)}
									{userData?.master_store?.recarga_sales_access === true &&
										!permissionsBlacklist?.includes('recargas') &&
										!isInactive && <MenuItem label="Recargas" path="/reportes/recargas" />}
								</MenuCategory>

								<MenuCategory label="Administración" Icon={SlidersHorizontal}>
									{!permissionsBlacklist?.includes('delete_sale') && !isInactive && (
										<MenuItem label="Eliminar venta" path="/deleteSale" />
									)}
									{!permissionsBlacklist?.includes('delete_payment') && !isInactive && (
										<MenuItem label="Eliminar pago" path="/tiendas/eliminar-pago" />
									)}
									{!permissionsBlacklist?.includes('change_sale_price') && !isInactive && (
										<MenuItem label="Cambiar precio de venta" path="/tiendas/cambiar-precio" />
									)}
									{!isInactive && <MenuItem label="Cambio por garantía" path="/warranty" />}
									{!isInactive && <MenuItem label="Cambiar teléfono" path="/tiendas/cambiar-telefono" />}
									{!isInactive && <MenuItem label="Cambiar correo" path="/tiendas/cambiar-email" />}
									{accessToOtherStores && <MenuItem label="Logear como" path="/log-in-as" />}
									{!userData.self_financed && userData.financed_by_krece && userData.bank_account && userData.master_user && (
										<MenuItem label="Información bancaria" path="/informacion-bancaria" />
									)}
									{userData?.master_store?.allow_iphone && (
										<MenuItem label="Búsqueda de iphones" path="/tiendas/buscar-iphones" />
									)}
								</MenuCategory>

								{helpVideos && helpVideos.length > 0 && (
									<MenuCategory label="Ayuda" Icon={HelpCircle}>
										<MenuItem label="Videos instructivos" path="/ayuda" />
									</MenuCategory>
								)}
							</>
						)}
						{internal && (
							<>
								{(!userData?.access_countries || userData.access_countries.length > 1) && (
									<Select
										containerClassName="pt-2"
										floatingLabel="Seleccionar país"
										options={
											userData?.access_countries
												? userData.access_countries.map((country) => ({
														label: country === 'rd' ? 'RD' : ucfirst(country),
														value: country
													}))
												: COUNTRIES.map((country) => ({ label: COUNTRY_LABELS[country], value: country }))
										}
										defaultValue={internalCountry.get() || userData.country}
										onSelectedChange={(value) => {
											internalCountry.set(value as any);
										}}
									/>
								)}
								<MenuCategory label="Búsqueda" Icon={UserSearchIcon}>
									{hasAccessToRoute('Administrar clientes') && <MenuItem label="Administrar clientes" path="/buscar-clientes" />}
									{hasAccessToRoute('Administrar tiendas') && <MenuItem label="Administrar tiendas" path="/manage-stores" />}
									{hasAccessToRoute('Buscar pagos de tienda') && (
										<MenuItem label="Buscar pagos de tienda" path="/buscar-pagos-de-tienda" />
									)}
								</MenuCategory>

								<MenuCategory label="Gestión de Pagos" Icon={NotepadText}>
									{hasAccessToRoute('Registrar pago') && <MenuItem label="Registrar pago" path="/registrar-pago" />}
									{hasAccessToRoute('Registrar pago manual') && (
										<MenuItem label="Registrar pago manual" path="/registrar-pago-manual" />
									)}
									{hasAccessToRoute('Pagos por verificar') && (
										<MenuItem label="Pagos por verificar" path="/pagos-por-verificar" />
									)}
									{hasAccessToRoute('Pagos crypto por verificar') && (
										<MenuItem label="Pagos crypto por verificar" path="/pagos-crypto-por-verificar" />
									)}
									{hasAccessToRoute('Pagar tarifa de recarga') && (
										<MenuItem label="Pagar tarifa de recarga" path="/pagar-tarifa-de-recarga" />
									)}
									{hasAccessToRoute('Simulador') && <MenuItem label="Simulador" path="/simulator" />}
								</MenuCategory>

								<MenuCategory label="Reportes" Icon={BarChart3}>
									{hasAccessToRoute('Ventas') && <MenuItem label="Ventas" path="/reportes/ventas" />}
									{hasAccessToRoute('Tiendas') && <MenuItem label="Tiendas" path="/reportes/tiendas" />}
									{hasAccessToRoute('Sucursales') && <MenuItem label="Sucursales" path="/reportes/sucursales" />}
									{hasAccessToRoute('Registros de actividad') && (
										<MenuItem label="Registros de actividad" path="/reportes/registros-de-actividad" />
									)}
								</MenuCategory>

								<MenuCategory label="Finanzas" Icon={Landmark}>
									{hasAccessToRoute('Pagos a aliados') && <MenuItem label="Pagos a aliados" path="/pagos-a-aliados" />}
									{canAccessSellerCommissions && <MenuItem label="Comisiones de vendedores" path="/comisiones-de-vendedores" />}
									{hasAccessToRoute('Configuración de financiamiento') && (
										<MenuItem label="Configuración de financiamiento" path="/configuracion-de-financiamiento" />
									)}
									{hasAccessToRoute('Conciliaciones') && <MenuItem label="Conciliaciones" path="/conciliaciones" />}
									{hasAccessToRoute('Bank account mappings') && (
										<MenuItem label="Bank account mappings" path="/bank-account-mappings" />
									)}
								</MenuCategory>

								<MenuCategory label="Operaciones" Icon={SlidersHorizontal}>
									{hasAccessToRoute('Pólizas de seguro') && <MenuItem label="Pólizas de seguro" path="/polizas-de-seguro" />}
									{hasAccessToRoute('Eliminación de préstamos') && (
										<MenuItem label="Eliminación de préstamos" path="/eliminacion-de-prestamos" />
									)}
									{hasAccessToRoute('MDM Routing') && <MenuItem label="MDM Routing" path="/mdm-routing" />}
									{hasAccessToRoute('Liberar IMEI') && <MenuItem label="Gestión de IMEIs" path="/liberar-imei" />}
								</MenuCategory>

								<MenuCategory label="Mercadeo" Icon={Megaphone}>
									{hasAccessToRoute('Notificaciones push') && (
										<MenuItem label="Notificaciones push" path="/notificaciones-push" />
									)}
									{hasAccessToRoute('Notificaciones ciclo de vida') && (
										<MenuItem label="Notificaciones ciclo de vida" path="/notificaciones-ciclo-de-vida" />
									)}
									{hasAccessToRoute('Configuración de promociones') && (
										<MenuItem label="Configuración de promociones" path="/configuracion-de-promociones" />
									)}
									{hasAccessToRoute('Campañas publicitarias') && (
										<MenuItem label="Campañas publicitarias" path="/campanas-publicitarias" />
									)}
									{hasAccessToRoute('Comunicado tiendas') && (
										<>
											<MenuItem label="Comunicados para tiendas" path="/comunicado-tiendas" />
											<MenuItem label="Pop-ups para tiendas" path="/popups-tiendas" />
										</>
									)}
									{hasAccessToRoute('Promociones') && <MenuItem label="Promociones" path="/promociones" />}
								</MenuCategory>

								<MenuCategory label="Administración" Icon={LayoutGrid}>
									{hasAccessToRoute('Solicitudes de tiendas') && <MenuItem label="Solicitudes de tiendas" path="/store-requests" />}
									{hasAccessToRoute('Control de acceso') && <MenuItem label="Control de acceso" path="/control-de-acceso" />}
									{hasAccessToRoute('Onboarding') && <MenuItem label="Onboarding" path="/onboarding" />}
									{hasAccessToRoute('Balance') && <MenuItem label="Balance" path="/kpis" />}
									{accessToOtherStores && <MenuItem label="Logear como" path="/log-in-as" />}
								</MenuCategory>
							</>
						)}
					</Accordion.Root>
				</ScrollArea.Viewport>
				<ScrollArea.Scrollbar
					className="duration-[160ms] flex touch-none select-none p-0.5 ease-out data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col"
					orientation="vertical"
				>
					<ScrollArea.Thumb className="relative flex-1 rounded-[10px] bg-neutral-800 transition-colors before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:bg-neutral-700" />
				</ScrollArea.Scrollbar>
			</ScrollArea.Root>

			<div className={classNames('w-full border-t-[1.5px] border-neutral-800/80', isDesktopCollapsed && 'xl:hidden')}>
				{intercomAvailable && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							openIntercom();
						}}
						className={classNames(
							'mx-4 mt-3 hidden w-[calc(100%-2rem)] items-center justify-center gap-2 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-200 transition-colors hover:bg-neutral-800 xl:flex light:bg-neutral-925',
							intercomOpen && 'border-primary-500/50 bg-primary-500/20 text-primary-200 light:text-primary-700'
						)}
					>
						<MessageCircle className="size-4" strokeWidth={2.25} />
						Soporte
					</button>
				)}
				{!internal && userData?.email && (
					<div className="truncate px-4 pt-3 text-xs text-neutral-400" title={userData.email}>
						{userData.email}
					</div>
				)}
				<div className="flex h-16 w-full items-center px-4">
					<button
						onClick={(e) => {
							e.stopPropagation();
							setTheme(theme === 'dark' ? 'light' : 'dark');
						}}
					>
						<MoonStar
							className={classNames('size-7 cursor-pointer text-neutral-0', { hidden: theme === 'light' })}
							strokeWidth={1.75}
						/>
						<SunMedium
							className={classNames('size-7 cursor-pointer text-neutral-0', { hidden: theme === 'dark' })}
							strokeWidth={1.75}
						/>
					</button>
					<div className="flex-1" />
					<div className="flex items-center gap-2">
						{helpVideos && helpVideos.length > 0 && (
							<Link
								to={'/ayuda'}
								className="flex items-center justify-center gap-1 rounded-lg border border-neutral-700 bg-neutral-900 p-1.5 text-sm text-neutral-200 transition-opacity hover:opacity-75 light:bg-neutral-925"
							>
								Ayuda
								<HelpCircle className="size-4" strokeWidth={2.5} />
							</Link>
						)}
						<button
							onClick={(e) => {
								e.stopPropagation();
								logout();
							}}
							className="flex items-center justify-center gap-1 rounded-lg border border-neutral-700 bg-neutral-900 p-1.5 text-sm text-neutral-200 transition-opacity hover:opacity-75 light:bg-neutral-925"
						>
							Salir
							<LogOut className="size-4" strokeWidth={2.5} />
						</button>
					</div>
				</div>
			</div>
		</nav>
	);
};

const TabletNavigation = ({
	setOpen,
	location,
	theme,
	setTheme,
	userData,
	permissionsBlacklist,
	routePermissions,
	intercomAvailable,
	intercomOpen,
	openIntercom
}: {
	location: Location;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	theme: 'dark' | 'light';
	setTheme: React.Dispatch<React.SetStateAction<'dark' | 'light'>>;
	userData: UserData;
	permissionsBlacklist: string[];
	routePermissions: ReturnType<typeof GetRoutePermissions>;
	intercomAvailable: boolean;
	intercomOpen: boolean;
	openIntercom: () => void;
}) => {
	const isInactive = userData?.is_active === false || userData?.master_store?.is_active === false;

	const MenuItem = ({
		label,
		path,
		Icon
	}: {
		label: string;
		path: string;
		Icon: React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'>>;
	}) => (
		<li>
			<Link
				to={path}
				className={classNames(
					'group flex flex-col items-center text-neutral-100 transition-colors duration-300 hover:text-primary-200 light:hover:text-primary-600',
					{
						'!text-primary-200 light:!text-primary-600': location.pathname === path
					}
				)}
			>
				<div
					className={classNames(
						'flex size-8 items-center justify-center rounded-lg text-neutral-100 transition-colors duration-300 group-hover:bg-primary-500/50 group-hover:text-neutral-100 light:group-hover:bg-primary-500/40',
						{
							'!bg-primary-500/50 !text-neutral-0 light:!bg-primary-500/40 light:!text-primary-800': location.pathname === path
						}
					)}
				>
					<Icon className="size-6" />
				</div>
				{label}
			</Link>
		</li>
	);

	const [scrollPosition, setScrollPosition] = useState(0);
	const scrollAreaRef = useRef<null | HTMLDivElement>(null);

	useEffect(() => {
		const handleScroll = () => {
			if (scrollAreaRef.current) {
				setScrollPosition(scrollAreaRef.current.scrollTop);
			}
		};

		const currentScrollArea = scrollAreaRef.current;
		if (currentScrollArea) {
			currentScrollArea.scrollTop = scrollPosition;
			currentScrollArea.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (currentScrollArea) {
				currentScrollArea.removeEventListener('scroll', handleScroll);
			}
		};
	});

	return (
		<nav className="fixed left-0 hidden h-svh w-16 shrink-0 flex-col items-center justify-between gap-8 bg-foreground light:border-r-[1.5px] light:border-neutral-800/80 md:flex xl:hidden">
			<Link to={'/'} className="flex h-20 w-full shrink-0 items-center justify-center border-b-[1.5px] border-neutral-800/80">
				<Logo size="min" className="size-9" />
			</Link>
			<ul className="flex shrink-0 flex-col gap-2">
				<li className={clsx(location.pathname === '/internal' && 'hidden')}>
					<Link
						to={'/'}
						className={classNames(
							'flex size-9 items-center justify-center rounded-lg text-neutral-100 transition-colors duration-300 hover:bg-primary-500/50 hover:text-neutral-0 light:hover:bg-primary-500/40',
							{
								'!bg-primary-500/50 !text-neutral-0 light:!bg-primary-500/40 light:!text-primary-800': location.pathname === '/'
							}
						)}
					>
						<Home className="size-6" />
					</Link>
				</li>
				<li
					className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-neutral-100 transition-colors hover:text-primary-200 light:hover:text-primary-600"
					onClick={(e) => {
						e.stopPropagation();
						setOpen(true);
					}}
				>
					<LayoutGrid className="size-6" />
				</li>
			</ul>

			<ScrollArea.Root className="h-full w-full overflow-hidden">
				<ScrollArea.Viewport ref={scrollAreaRef} className="h-full w-full">
					<ul
						className={clsx(
							'flex shrink-0 flex-col gap-5 text-[0.7rem] font-semibold tracking-tight',
							location.pathname === '/internal' && 'hidden'
						)}
					>
						{!permissionsBlacklist?.includes('new_sale') && !isInactive && (
							<MenuItem label="Registro" path="/newSale" Icon={Plus} />
						)}
						<MenuItem label="Ventas" path="/reportes/ventas" Icon={Sheet} />
						{userData.master_user && <MenuItem label="Salud" path="/salud" Icon={Activity} />}
						{!permissionsBlacklist?.includes('register_payment') && (
							<MenuItem label="Clientes" path="/buscarcliente" Icon={UserSearch} />
						)}
					</ul>
				</ScrollArea.Viewport>
				<ScrollArea.Scrollbar
					className="duration-[160ms] flex touch-none select-none p-0.5 ease-out data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col"
					orientation="vertical"
				>
					<ScrollArea.Thumb className="relative flex-1 rounded-[10px] bg-neutral-800 transition-colors before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:bg-neutral-700" />
				</ScrollArea.Scrollbar>
			</ScrollArea.Root>

			<div className="flex w-full shrink-0 flex-col items-center justify-center gap-4 border-t-[1.5px] border-neutral-800/80 py-4">
				{intercomAvailable && (
					<button
						type="button"
						aria-label="Abrir soporte"
						title="Soporte"
						onClick={(e) => {
							e.stopPropagation();
							openIntercom();
						}}
						className={classNames(
							'flex size-9 items-center justify-center rounded-lg text-neutral-100 transition-colors hover:bg-primary-500/50 hover:text-neutral-0 light:hover:bg-primary-500/40',
							intercomOpen && 'bg-primary-500/50 text-neutral-0 light:bg-primary-500/40 light:text-primary-800'
						)}
					>
						<MessageCircle className="size-6" />
					</button>
				)}
				<button
					className={classNames('flex size-9 shrink-0 items-center justify-center')}
					onClick={(e) => {
						e.stopPropagation();
						setTheme(theme === 'dark' ? 'light' : 'dark');
					}}
				>
					<MoonStar
						className={classNames('size-6 shrink-0 cursor-pointer text-neutral-0', { hidden: theme === 'light' })}
						strokeWidth={1.75}
					/>
					<SunMedium
						className={classNames('size-6 shrink-0 cursor-pointer text-neutral-0', { hidden: theme === 'dark' })}
						strokeWidth={1.75}
					/>
				</button>

				<button
					onClick={(e) => {
						e.stopPropagation();
						logout();
					}}
					className="flex size-9 items-center justify-center gap-2 rounded-lg bg-neutral-900 transition-colors hover:bg-neutral-700"
				>
					<LogOut className="size-7 text-neutral-300" />
				</button>
			</div>
		</nav>
	);
};

const MobileNavigation = ({
	setOpen,
	location,
	permissionsBlacklist,
	userData,
	routePermissions,
	intercomAvailable,
	intercomOpen,
	openIntercom
}: {
	location: Location;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	permissionsBlacklist: string[];
	userData: UserData;
	routePermissions: ReturnType<typeof GetRoutePermissions>;
	intercomAvailable: boolean;
	intercomOpen: boolean;
	openIntercom: () => void;
}) => {
	const isInactive = userData?.is_active === false || userData?.master_store?.is_active === false;

	return (
		<nav className="fixed bottom-0 flex h-[4.5rem] w-full shrink-0 items-center border-t-[1.5px] border-neutral-800/80 bg-foreground px-2 py-2 min-[400px]:px-4 md:hidden md:border-t-0">
			<ul className="grid w-full grid-flow-col auto-cols-fr items-center text-xs font-semibold tracking-tight [&>li]:justify-self-center">
				<li className={clsx(location.pathname === '/internal' && 'hidden')}>
					<Link
						to={'/'}
						className={classNames(
							'group flex flex-col items-center text-neutral-100 transition-colors duration-300 hover:text-primary-200 light:hover:text-primary-600',
							{
								'!text-primary-200 light:!text-primary-600': location.pathname === '/'
							}
						)}
					>
						<div
							className={classNames(
								'flex size-9 items-center justify-center rounded-lg text-neutral-100 transition-colors duration-300 group-hover:bg-primary-500/50 group-hover:text-neutral-100 light:group-hover:bg-primary-500/40',
								{
									'!bg-primary-500/50 !text-neutral-0 light:!bg-primary-500/40': location.pathname === '/'
								}
							)}
						>
							<Logo
								size="min"
								className="size-6"
								fill={
									location.pathname === '/'
										? 'light:fill-primary-800 fill-neutral-100'
										: 'fill-neutral-100 light:fill-neutral-200'
								}
							/>
						</div>
						Inicio
					</Link>
				</li>
				<li
					className={clsx(
						(location.pathname === '/internal' || permissionsBlacklist?.includes('new_sale') || isInactive) && 'hidden'
					)}
				>
					<Link
						to={'/newSale'}
						className={classNames(
							'group flex flex-col items-center text-neutral-100 transition-colors duration-300 hover:text-primary-200 light:hover:text-primary-600',
							{
								'!text-primary-200 light:!text-primary-600': location.pathname === '/newSale'
							}
						)}
					>
						<div
							className={classNames(
								'flex size-9 items-center justify-center rounded-lg text-neutral-100 transition-colors duration-300 group-hover:bg-primary-500/50 group-hover:text-neutral-100 light:group-hover:bg-primary-500/40',
								{
									'!bg-primary-500/50 !text-neutral-0 light:!bg-primary-500/40 light:!text-primary-800':
										location.pathname === '/newSale'
								}
							)}
						>
							<Plus className="size-6 scale-110" strokeWidth={2.5} />
						</div>
						Registro
					</Link>
				</li>
				<li className={clsx(location.pathname === '/internal' && 'hidden')}>
					<Link
						to={'/reportes/ventas'}
						className={classNames(
							'group flex flex-col items-center text-neutral-100 transition-colors duration-300 hover:text-primary-200 light:hover:text-primary-600',
							{
								'!text-primary-200 light:!text-primary-600': location.pathname === '/reportes/ventas'
							}
						)}
					>
						<div
							className={classNames(
								'flex size-9 items-center justify-center rounded-lg text-neutral-100 transition-colors duration-300 group-hover:bg-primary-500/50 group-hover:text-neutral-100 light:group-hover:bg-primary-500/40',
								{
									'!bg-primary-500/50 !text-neutral-0 light:!bg-primary-500/40 light:!text-primary-800':
										location.pathname === '/reportes/ventas'
								}
							)}
						>
							<Sheet className="size-6" />
						</div>
						Ventas
					</Link>
				</li>
				{!permissionsBlacklist?.includes('register_payment') && (
					<li className={clsx(location.pathname === '/internal' && 'hidden')}>
						<Link
							to={'/buscarcliente'}
							className={classNames(
								'group flex flex-col items-center text-neutral-100 transition-colors duration-300 hover:text-primary-200 light:hover:text-primary-600',
								{
									'!text-primary-200 light:!text-primary-600': location.pathname === '/buscarcliente'
								}
							)}
						>
							<div
								className={classNames(
									'flex size-9 items-center justify-center rounded-lg text-neutral-100 transition-colors duration-300 group-hover:bg-primary-500/50 group-hover:text-neutral-100 light:group-hover:bg-primary-500/40',
									{
										'!bg-primary-500/50 !text-neutral-0 light:!bg-primary-500/40 light:!text-primary-800':
											location.pathname === '/buscarcliente'
									}
								)}
							>
								<UserSearch className="size-6" strokeWidth={2.25} />
							</div>
							Clientes
						</Link>
					</li>
				)}
				{intercomAvailable && (
					<li>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								openIntercom();
							}}
							className={classNames(
								'group flex flex-col items-center text-neutral-100 transition-colors duration-300 hover:text-primary-200 light:hover:text-primary-600',
								intercomOpen && 'text-primary-200 light:text-primary-600'
							)}
						>
							<div
								className={classNames(
									'flex size-9 items-center justify-center rounded-lg text-neutral-100 transition-colors duration-300 group-hover:bg-primary-500/50 group-hover:text-neutral-100 light:group-hover:bg-primary-500/40',
									intercomOpen && 'bg-primary-500/50 text-neutral-0 light:bg-primary-500/40 light:text-primary-800'
								)}
							>
								<MessageCircle className="size-6" />
							</div>
							Soporte
						</button>
					</li>
				)}
				<li>
					<button
						onClick={(e) => {
							e.stopPropagation();
							setOpen(true);
						}}
					>
						<Menu className="size-9 text-neutral-0 transition-colors hover:text-primary-200" />
					</button>
				</li>
			</ul>
		</nav>
	);
};

const Overlay = () => (
	<div className={classNames('fixed z-20 size-full animate-[fadeOverlayIn_350ms_ease-in_forwards] bg-black opacity-0')}></div>
);

export default function Navigation({
	CountrySettings,
	userData,
	accessToOtherStores,
	internal,
	internalPath,
	internalCountry,
	permissionsBlacklist,
	permissionsWhitelist,
	routePermissions,
	isDesktopCollapsed,
	setDesktopCollapsed,
	isAdminAccount,
	sellerCommissionsAvailable,
	intercomAvailable,
	intercomOpen,
	openIntercom
}: {
	CountrySettings: CountrySettings | null;
	userData: UserData;
	accessToOtherStores: string;
	internal: boolean;
	internalPath: { get: () => string; set: (path: string) => void };
	internalCountry: { get: () => string; set: (country: string) => void };
	permissionsBlacklist: string[];
	permissionsWhitelist: string[];
	routePermissions: ReturnType<typeof GetRoutePermissions>;
	isDesktopCollapsed: boolean;
	setDesktopCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	isAdminAccount: boolean;
	sellerCommissionsAvailable: boolean;
	intercomAvailable: boolean;
	intercomOpen: boolean;
	openIntercom: () => void;
}) {
	const location = useLocation();

	const [theme, setTheme] = useState<'dark' | 'light'>(getInitialTheme);
	const [isOpen, setOpen] = useState(false);

	// Hide menu on outside click
	useEffect(() => {
		const page = document.getElementById('root')?.firstElementChild;
		if (!page) return;

		const handleClick = () => setOpen(false);
		document.addEventListener('click', handleClick);

		return () => {
			document.removeEventListener('click', handleClick);
		};
	});

	// Update theme in localStorage and DOM
	useEffect(() => {
		localStorage.setItem('theme', theme);
		document.documentElement.classList.remove('dark', 'light');
		document.documentElement.classList.add(theme);
	}, [theme]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!internal) return;

			const target = event.target as HTMLElement | null;
			const isEditing =
				target?.tagName === 'INPUT' ||
				target?.tagName === 'TEXTAREA' ||
				target?.tagName === 'SELECT' ||
				target?.isContentEditable;

			if (isEditing || !event.ctrlKey || event.key.toLowerCase() !== 'b') return;
			if (!window.matchMedia('(min-width: 1280px)').matches) return;

			event.preventDefault();
			setDesktopCollapsed((collapsed) => !collapsed);
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [internal, setDesktopCollapsed]);

	const helpVideos = CountrySettings?.instructions || [];

	return (
		<>
			<DesktopNavigation
				userData={userData}
				isOpen={isOpen}
				setOpen={setOpen}
				isDesktopCollapsed={isDesktopCollapsed}
				setDesktopCollapsed={setDesktopCollapsed}
				isAdminAccount={isAdminAccount}
				sellerCommissionsAvailable={sellerCommissionsAvailable}
				location={location}
				theme={theme}
				setTheme={setTheme}
				accessToOtherStores={accessToOtherStores}
				helpVideos={helpVideos}
				featureFlags={CountrySettings?.feature_flags}
				internal={internal}
				internalPath={internalPath}
				internalCountry={internalCountry}
				permissionsBlacklist={permissionsBlacklist}
				permissionsWhitelist={permissionsWhitelist}
				routePermissions={routePermissions}
				intercomAvailable={intercomAvailable}
				intercomOpen={intercomOpen}
				openIntercom={openIntercom}
			/>
			<TabletNavigation
				userData={userData}
				setOpen={setOpen}
				location={location}
				theme={theme}
				setTheme={setTheme}
				permissionsBlacklist={permissionsBlacklist}
				routePermissions={routePermissions}
				intercomAvailable={intercomAvailable}
				intercomOpen={intercomOpen}
				openIntercom={openIntercom}
			/>
			<MobileNavigation
				userData={userData}
				setOpen={setOpen}
				location={location}
				permissionsBlacklist={permissionsBlacklist}
				routePermissions={routePermissions}
				intercomAvailable={intercomAvailable}
				intercomOpen={intercomOpen}
				openIntercom={openIntercom}
			/>

			{isOpen && <Overlay />}
		</>
	);
}
